import { useContext, useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller, useFieldArray, useWatch } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  InputAdornment,
  Tooltip,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import StarIcon from '@mui/icons-material/Star';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import axiosInstance from '../../lib/axios';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { useSnackbar } from '../../components/SnackbarProvider';
import { QuickSupplierModal } from '../pos/components/QuickSupplierModal';
import { BarcodeProductSearch } from '../../components/barcode/BarcodeProductSearch';
import { searchProductByBarcode } from '../../hooks/useBarcodeProductSearch';
import type { Supplier, Location, Product } from '../../types';

interface FormItemData {
  productId: string;
  productName: string;
  quantity: number;
  unitCost: number;
}

interface FormData {
  supplierId: string;
  locationId: string;
  expectedDeliveryDate: string;
  notes: string;
  items: FormItemData[];
}

interface CreateRestockDto {
  supplierId: string;
  locationId: string;
  expectedDeliveryDate?: string;
  notes?: string;
  items: {
    productId: string;
    quantity: number;
    unitCost: number;
  }[];
}

interface ProductPurchaseFrequency {
  productId: string;
  productName: string;
  totalQuantity: number;
  orderCount: number;
  lastOrderDate: string | null;
}

interface ProductWithFrequency extends Product {
  isFrequent?: boolean;
  orderCount?: number;
}

const fetchSuppliers = async (): Promise<Supplier[]> => {
  const response = await axiosInstance.get('/suppliers', { params: { isActive: true, limit: 100 } });
  return Array.isArray(response.data) ? response.data : response.data.items || [];
};

const fetchLocations = async (): Promise<Location[]> => {
  const response = await axiosInstance.get('/locations');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const fetchProducts = async (): Promise<Product[]> => {
  const response = await axiosInstance.get('/products?limit=100');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const fetchSupplierProductFrequency = async (supplierId: string): Promise<ProductPurchaseFrequency[]> => {
  const response = await axiosInstance.get(`/suppliers/${supplierId}/product-frequency`, { params: { limit: 50 } });
  return Array.isArray(response.data) ? response.data : [];
};

export const RestockCreate = () => {
  const { id } = useParams<{ id?: string }>();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mode } = useContext(ColorModeContext);
  const { showMessage } = useSnackbar();
  const animations = useAnimationStyles();

  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [quickSupplierModalOpen, setQuickSupplierModalOpen] = useState(false);
  const [formInitialized, setFormInitialized] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  // Default colors for QuickSupplierModal when used outside POS context
  const quickModalColors = useMemo(() => ({
    bg: mode === 'dark' ? palette.background : palette.light.background,
    surface: mode === 'dark' ? palette.surface : palette.light.surface,
    surfaceLight: mode === 'dark' ? palette.surfaceElevated : palette.light.surfaceElevated,
    border: mode === 'dark' ? palette.border : palette.light.border,
    textPrimary: mode === 'dark' ? palette.textPrimary : palette.light.textPrimary,
    textSecondary: mode === 'dark' ? palette.textSecondary : palette.light.textSecondary,
    textMuted: mode === 'dark' ? palette.textMuted : palette.light.textMuted,
    accent: palette.primary,
    accentLight: palette.primaryLight,
  }), [mode]);

  const { data: suppliers, isLoading: suppliersLoading } = useQuery({
    queryKey: ['suppliers-active'],
    queryFn: fetchSuppliers,
  });

  const { data: locations, isLoading: locationsLoading } = useQuery({
    queryKey: ['locations'],
    queryFn: fetchLocations,
  });

  const { data: products, isLoading: productsLoading } = useQuery({
    queryKey: ['products'],
    queryFn: fetchProducts,
  });

  // Fetch product frequency for the selected supplier
  const { data: productFrequency } = useQuery({
    queryKey: ['supplier-product-frequency', selectedSupplier?.id],
    queryFn: () => fetchSupplierProductFrequency(selectedSupplier!.id),
    enabled: !!selectedSupplier,
  });

  // Fetch existing order when in edit mode
  const { data: existingOrder } = useQuery({
    queryKey: ['bulk-restock-order', id],
    queryFn: async () => {
      const response = await axiosInstance.get(`/restocks/${id}`);
      return response.data;
    },
    enabled: isEditMode,
  });

  // Sort products: frequently ordered products first, then others alphabetically
  const sortedProducts = useMemo((): ProductWithFrequency[] => {
    if (!products) return [];

    // Create a map of product frequency data
    const frequencyMap = new Map<string, ProductPurchaseFrequency>();
    if (productFrequency) {
      productFrequency.forEach((freq) => {
        frequencyMap.set(freq.productId, freq);
      });
    }

    // Enrich products with frequency data
    const enrichedProducts: ProductWithFrequency[] = products.map((product) => {
      const freq = frequencyMap.get(product.id);
      return {
        ...product,
        isFrequent: !!freq,
        orderCount: freq?.orderCount || 0,
      };
    });

    // Sort: frequent products first (by order count desc), then others alphabetically
    return enrichedProducts.sort((a, b) => {
      // Both frequent: sort by order count desc
      if (a.isFrequent && b.isFrequent) {
        return (b.orderCount || 0) - (a.orderCount || 0);
      }
      // Only a is frequent: a comes first
      if (a.isFrequent) return -1;
      // Only b is frequent: b comes first
      if (b.isFrequent) return 1;
      // Neither frequent: sort alphabetically
      return a.name.localeCompare(b.name);
    });
  }, [products, productFrequency]);

  const defaultExpectedDeliveryDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  }, []);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      supplierId: '',
      locationId: '',
      expectedDeliveryDate: defaultExpectedDeliveryDate,
      notes: '',
      items: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const rawItems = useWatch({ control, name: 'items' });

  // Populate form when editing an existing order
  useEffect(() => {
    if (isEditMode && existingOrder && suppliers && locations && !formInitialized) {
      // Set supplier
      const supplier = suppliers.find((s) => s.id === existingOrder.supplierId);
      if (supplier) setSelectedSupplier(supplier);

      // Set location
      const location = locations.find((l) => l.id === existingOrder.locationId);
      if (location) setSelectedLocation(location);

      // Reset form with existing data
      reset({
        supplierId: existingOrder.supplierId,
        locationId: existingOrder.locationId,
        expectedDeliveryDate: existingOrder.expectedDeliveryDate?.split('T')[0] || defaultExpectedDeliveryDate,
        notes: existingOrder.notes || '',
        items: existingOrder.items?.map((item: any) => ({
          productId: item.productId,
          productName: item.productName || item.product?.name || '',
          quantity: item.quantity,
          unitCost: item.unitCost,
        })) || [],
      });

      setFormInitialized(true);
    }
  }, [isEditMode, existingOrder, suppliers, locations, formInitialized, reset, defaultExpectedDeliveryDate]);

  // Auto-focus barcode input when page loads (for USB scanner ready)
  useEffect(() => {
    const timer = setTimeout(() => {
      barcodeInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Use watched items directly (useWatch already handles reactivity); wrapped in its own
  // useMemo so `|| []` doesn't create a new array reference on every render, which would
  // otherwise defeat the `totals` useMemo below that depends on it.
  const watchItems = useMemo(() => rawItems || [], [rawItems]);

  // Calculate totals
  const totals = useMemo(() => {
    const subtotal = watchItems.reduce((sum, item) => {
      const qty = Number(item?.quantity) || 0;
      const cost = Number(item?.unitCost) || 0;
      return sum + qty * cost;
    }, 0);
    // No tax for purchase orders (tax is handled separately)
    return { subtotal, total: subtotal };
  }, [watchItems]);

  const saveMutation = useMutation({
    mutationFn: (data: CreateRestockDto) =>
      isEditMode
        ? axiosInstance.put(`/restocks/${id}`, data)
        : axiosInstance.post('/restocks', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['restocks'] });
      if (isEditMode) {
        queryClient.invalidateQueries({ queryKey: ['bulk-restock-order', id] });
        showMessage('Commande mise à jour avec succès', 'success');
        navigate(`/app/restocks/${id}`);
      } else {
        showMessage('Commande créée avec succès', 'success');
        navigate('/app/restocks');
      }
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || (isEditMode ? 'Erreur lors de la mise à jour' : 'Erreur lors de la création de la commande'),
        'error'
      );
    },
  });

  const onSubmit = (data: FormData) => {
    if (!selectedSupplier) {
      showMessage('Veuillez sélectionner un fournisseur', 'error');
      return;
    }

    if (!selectedLocation) {
      showMessage('Veuillez sélectionner un emplacement de réception', 'error');
      return;
    }

    if (data.items.length === 0) {
      showMessage('Veuillez ajouter au moins un produit', 'error');
      return;
    }

    // Validate all items have valid quantities and costs
    const invalidItems = data.items.filter(
      (item) => !item.quantity || item.quantity < 1 || item.unitCost < 0
    );
    if (invalidItems.length > 0) {
      showMessage('Veuillez vérifier les quantités et coûts des produits', 'error');
      return;
    }

    const payload: CreateRestockDto = {
      supplierId: selectedSupplier.id,
      locationId: selectedLocation.id,
      expectedDeliveryDate: data.expectedDeliveryDate || undefined,
      notes: data.notes || undefined,
      items: data.items.map((item) => ({
        productId: item.productId,
        quantity: Number(item.quantity),
        unitCost: Number(item.unitCost),
      })),
    };

    saveMutation.mutate(payload);
  };

  const handleAddProduct = (product: Product | null) => {
    if (product) {
      // Check if product is already in the list
      const existingIndex = fields.findIndex((f) => f.productId === product.id);
      if (existingIndex >= 0) {
        showMessage('Ce produit est déjà dans la liste', 'warning');
        return;
      }

      append({
        productId: product.id,
        productName: product.name,
        quantity: 1,
        unitCost: product.defaultCostPrice || product.cost || 0,
      });
    }
  };

  // Handle product found via barcode scanner
  const handleBarcodeProductFound = (product: Product, _barcode: string) => {
    // Check if product is already in the list - if so, show warning
    const existingIndex = fields.findIndex((f) => f.productId === product.id);
    if (existingIndex >= 0) {
      showMessage('Ce produit est déjà dans la liste', 'warning');
      return;
    }

    // Add product to the items list
    append({
      productId: product.id,
      productName: product.name,
      quantity: 1,
      unitCost: product.defaultCostPrice || (product as any).cost || 0,
    });
    showMessage(`${product.name} ajouté à la commande`, 'success');
  };

  // Handle manual barcode input (for USB scanner/douchette)
  const handleBarcodeInputSearch = async (barcode: string) => {
    if (!barcode.trim()) return;

    setBarcodeInput(''); // Clear input immediately

    try {
      const result = await searchProductByBarcode(barcode.trim());
      if (result.found && result.product) {
        handleBarcodeProductFound(result.product, barcode);
      } else {
        showMessage('Produit non trouvé pour ce code-barres', 'warning');
      }
    } catch (error: any) {
      showMessage(error.response?.data?.message || 'Erreur de recherche', 'error');
    }

    barcodeInputRef.current?.focus();
  };

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: colors.surfaceElevated,
      borderRadius: theme.radius.sm,
      '& fieldset': { borderColor: colors.border },
      '&:hover fieldset': { borderColor: palette.primary },
      '&.Mui-focused fieldset': { borderColor: palette.primary },
    },
    '& .MuiInputLabel-root': { color: colors.textMuted },
    '& .MuiInputBase-input': { color: colors.textPrimary },
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', { style: 'currency', currency: 'DZD' }).format(amount);
  };

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Header */}
      <Box sx={{ mb: 4, ...animations.slideDown }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/app/restocks')}
          sx={{ color: colors.textMuted, mb: 2, '&:hover': { color: palette.primary } }}
        >
          Retour aux commandes
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <LocalShippingIcon sx={{ color: palette.primary, fontSize: 24 }} />
          <Typography
            variant="overline"
            sx={{
              color: palette.primary,
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              letterSpacing: typography.letterSpacing.widest,
              fontWeight: typography.fontWeight.bold,
            }}
          >
            GESTION DES ACHATS
          </Typography>
        </Box>
        <Typography
          variant="h3"
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.extrabold,
            color: colors.textPrimary,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
          }}
        >
          {isEditMode ? 'MODIFIER LA COMMANDE' : 'NOUVELLE COMMANDE FOURNISSEUR'}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
          {/* Main Content */}
          <Box>
            {/* Supplier & Location Selection */}
            <Card
              sx={{
                backgroundColor: colors.surface,
                border: `2px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                p: 3,
                mb: 3,
                ...animations.slideUp(0.1),
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.display,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.textPrimary,
                  mb: 3,
                  pb: 1,
                  borderBottom: `2px solid ${colors.border}`,
                }}
              >
                DÉTAILS DE LA COMMANDE
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Autocomplete
                    options={suppliers || []}
                    loading={suppliersLoading}
                    value={selectedSupplier}
                    onChange={(_, value) => setSelectedSupplier(value)}
                    getOptionLabel={(option) => `${option.name} (${option.code})`}
                    sx={{ flex: 1 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Fournisseur *"
                        error={!selectedSupplier && !!errors.supplierId}
                        sx={inputStyles}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {suppliersLoading ? <CircularProgress size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <Tooltip title="Nouveau fournisseur">
                    <IconButton
                      onClick={() => setQuickSupplierModalOpen(true)}
                      sx={{
                        mt: '8px',
                        backgroundColor: alpha(palette.primary, 0.1),
                        color: palette.primary,
                        border: `1px solid ${palette.primary}`,
                        '&:hover': {
                          backgroundColor: alpha(palette.primary, 0.2),
                        },
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Autocomplete
                  options={locations || []}
                  loading={locationsLoading}
                  value={selectedLocation}
                  onChange={(_, value) => setSelectedLocation(value)}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Emplacement de réception *"
                      error={!selectedLocation && !!errors.locationId}
                      sx={inputStyles}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {locationsLoading ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <TextField
                  {...register('expectedDeliveryDate')}
                  label="Date de livraison prévue"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={inputStyles}
                />
              </Box>
            </Card>

            {/* Line Items */}
            <Card
              sx={{
                backgroundColor: colors.surface,
                border: `2px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                p: 3,
                mb: 3,
                ...animations.slideUp(0.15),
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                  pb: 1,
                  borderBottom: `2px solid ${colors.border}`,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: typography.fontFamily.display,
                    fontWeight: typography.fontWeight.bold,
                    color: colors.textPrimary,
                  }}
                >
                  PRODUITS À COMMANDER
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    size="small"
                    inputRef={barcodeInputRef}
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleBarcodeInputSearch(barcodeInput);
                      }
                    }}
                    placeholder="Code-barres..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <QrCodeScannerIcon sx={{ color: palette.primary, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      ...inputStyles,
                      width: 200,
                      '& .MuiOutlinedInput-root': {
                        ...inputStyles['& .MuiOutlinedInput-root'],
                        borderColor: palette.primary,
                      },
                    }}
                  />
                  <BarcodeProductSearch
                    onProductFound={handleBarcodeProductFound}
                    continuousMode={true}
                    colors={quickModalColors}
                    buttonSize="medium"
                    buttonTooltip="Scanner un code-barres pour ajouter un produit"
                    buttonSx={{
                      backgroundColor: alpha(palette.primary, 0.1),
                      border: `1px solid ${palette.primary}`,
                      '&:hover': {
                        backgroundColor: alpha(palette.primary, 0.2),
                      },
                    }}
                  />
                <Autocomplete
                  options={sortedProducts}
                  loading={productsLoading}
                  onChange={(_, value) => handleAddProduct(value)}
                  getOptionLabel={(option) => `${option.name} (${option.sku})`}
                  groupBy={selectedSupplier ? (option) => (option as ProductWithFrequency).isFrequent ? 'Produits fréquents' : 'Autres produits' : undefined}
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 2,
                          py: 1,
                          backgroundColor: alpha(colors.border, 0.5),
                          position: 'sticky',
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        {params.group === 'Produits fréquents' && (
                          <StarIcon sx={{ fontSize: 16, color: palette.warning }} />
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: typography.fontFamily.mono,
                            fontWeight: typography.fontWeight.bold,
                            color: params.group === 'Produits fréquents' ? palette.warning : colors.textMuted,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          {params.group}
                        </Typography>
                      </Box>
                      <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                    </li>
                  )}
                  renderOption={(props, option) => {
                    const productOption = option as ProductWithFrequency;
                    return (
                      <li {...props} key={option.id}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2">{option.name}</Typography>
                              {productOption.isFrequent && (
                                <Chip
                                  size="small"
                                  icon={<StarIcon sx={{ fontSize: '14px !important' }} />}
                                  label={`${productOption.orderCount} commandes`}
                                  sx={{
                                    height: 20,
                                    fontSize: typography.fontSize.xs,
                                    backgroundColor: alpha(palette.warning, 0.15),
                                    color: palette.warning,
                                    '& .MuiChip-icon': { color: palette.warning },
                                  }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              SKU: {option.sku} | Coût: {formatCurrency(option.defaultCostPrice || option.cost || 0)}
                            </Typography>
                          </Box>
                        </Box>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={selectedSupplier ? 'Ajouter un produit (triés par fréquence)' : 'Ajouter un produit'}
                      size="small"
                      sx={{ ...inputStyles, width: 350 }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {productsLoading ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  value={null}
                />
                </Box>
              </Box>

              {fields.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography sx={{ color: colors.textMuted }}>
                    Aucun produit ajouté. Utilisez le champ ci-dessus pour ajouter des produits.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            color: colors.textMuted,
                            fontFamily: typography.fontFamily.mono,
                            fontSize: typography.fontSize.xs,
                          }}
                        >
                          Produit
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            color: colors.textMuted,
                            fontFamily: typography.fontFamily.mono,
                            fontSize: typography.fontSize.xs,
                          }}
                        >
                          Quantité
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: colors.textMuted,
                            fontFamily: typography.fontFamily.mono,
                            fontSize: typography.fontSize.xs,
                          }}
                        >
                          Coût unitaire
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: colors.textMuted,
                            fontFamily: typography.fontFamily.mono,
                            fontSize: typography.fontSize.xs,
                          }}
                        >
                          Sous-total
                        </TableCell>
                        <TableCell width={50}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fields.map((field, index) => {
                        const item = watchItems[index];
                        const qty = Number(item?.quantity) || 0;
                        const cost = Number(item?.unitCost) || 0;
                        const itemSubtotal = qty * cost;

                        return (
                          <TableRow key={field.id}>
                            <TableCell>
                              <Typography
                                sx={{
                                  color: colors.textPrimary,
                                  fontWeight: typography.fontWeight.medium,
                                }}
                              >
                                {field.productName}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Controller
                                name={`items.${index}.quantity` as const}
                                control={control}
                                render={({ field: f }) => (
                                  <TextField
                                    {...f}
                                    type="number"
                                    size="small"
                                    sx={{ ...inputStyles, width: 80 }}
                                    inputProps={{ min: 1, style: { textAlign: 'center' } }}
                                    onChange={(e) => f.onChange(Number(e.target.value) || 1)}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Controller
                                name={`items.${index}.unitCost` as const}
                                control={control}
                                render={({ field: f }) => (
                                  <TextField
                                    {...f}
                                    type="number"
                                    size="small"
                                    sx={{ ...inputStyles, width: 120 }}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">DZD</InputAdornment>
                                      ),
                                    }}
                                    inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                                    onChange={(e) => f.onChange(Number(e.target.value) || 0)}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                sx={{
                                  fontFamily: typography.fontFamily.mono,
                                  fontWeight: typography.fontWeight.bold,
                                  color: palette.success,
                                }}
                              >
                                {formatCurrency(itemSubtotal)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => remove(index)}
                                sx={{ color: palette.error }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>

            {/* Notes */}
            <Card
              sx={{
                backgroundColor: colors.surface,
                border: `2px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                p: 3,
                ...animations.slideUp(0.2),
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.display,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.textPrimary,
                  mb: 3,
                  pb: 1,
                  borderBottom: `2px solid ${colors.border}`,
                }}
              >
                NOTES
              </Typography>

              <TextField
                {...register('notes')}
                label="Notes de commande"
                multiline
                rows={3}
                fullWidth
                sx={inputStyles}
                placeholder="Instructions spéciales, conditions de livraison, etc."
              />
            </Card>
          </Box>

          {/* Sidebar - Totals */}
          <Box>
            <Card
              sx={{
                backgroundColor: colors.surface,
                border: `2px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                p: 3,
                position: 'sticky',
                top: 20,
                ...animations.slideUp(0.1),
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.display,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.textPrimary,
                  mb: 3,
                  pb: 1,
                  borderBottom: `2px solid ${colors.border}`,
                }}
              >
                RÉSUMÉ
              </Typography>

              {/* Order Summary */}
              <Box sx={{ mb: 3 }}>
                {selectedSupplier && (
                  <Box sx={{ mb: 2, p: 2, backgroundColor: colors.surfaceElevated, borderRadius: theme.radius.sm }}>
                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                      Fournisseur
                    </Typography>
                    <Typography sx={{ color: colors.textPrimary, fontWeight: typography.fontWeight.medium }}>
                      {selectedSupplier.name}
                    </Typography>
                  </Box>
                )}
                {selectedLocation && (
                  <Box sx={{ mb: 2, p: 2, backgroundColor: colors.surfaceElevated, borderRadius: theme.radius.sm }}>
                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                      Livraison à
                    </Typography>
                    <Typography sx={{ color: colors.textPrimary, fontWeight: typography.fontWeight.medium }}>
                      {selectedLocation.name}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: colors.textMuted }}>Nombre de produits:</Typography>
                  <Typography sx={{ fontFamily: typography.fontFamily.mono, color: colors.textPrimary }}>
                    {fields.length}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ color: colors.textMuted }}>Quantité totale:</Typography>
                  <Typography sx={{ fontFamily: typography.fontFamily.mono, color: colors.textPrimary }}>
                    {watchItems.reduce((sum, item) => sum + (Number(item?.quantity) || 0), 0)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2, borderTop: `2px solid ${colors.border}` }}>
                  <Typography sx={{ fontWeight: typography.fontWeight.bold, color: colors.textPrimary }}>
                    TOTAL:
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      fontWeight: typography.fontWeight.extrabold,
                      color: palette.success,
                      fontSize: typography.fontSize.lg,
                    }}
                  >
                    {formatCurrency(totals.total)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  startIcon={saveMutation.isPending ? <CircularProgress size={16} /> : <SaveIcon />}
                  disabled={saveMutation.isPending || fields.length === 0}
                  sx={{
                    backgroundColor: palette.primary,
                    color: '#FFFFFF',
                    fontWeight: typography.fontWeight.bold,
                    py: 1.5,
                    '&:hover': { backgroundColor: alpha(palette.primary, 0.8) },
                  }}
                >
                  {saveMutation.isPending
                    ? (isEditMode ? 'Mise à jour...' : 'Création...')
                    : (isEditMode ? 'Mettre à jour' : 'Créer la commande')}
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/app/restocks')}
                  sx={{
                    borderColor: colors.border,
                    color: colors.textPrimary,
                    '&:hover': { borderColor: colors.textMuted },
                  }}
                >
                  Annuler
                </Button>
              </Box>

              {/* Status Note */}
              {!isEditMode && (
                <Box sx={{ mt: 3, p: 2, backgroundColor: alpha(palette.info, 0.1), borderRadius: theme.radius.sm }}>
                  <Typography variant="caption" sx={{ color: palette.info }}>
                    La commande sera créée avec le statut "Devis". Vous pourrez ensuite la confirmer,
                    la marquer comme livrée (ce qui mettra à jour le stock), puis comme payée.
                  </Typography>
                </Box>
              )}
            </Card>
          </Box>
        </Box>
      </form>

      {/* Quick Supplier Creation Modal */}
      <QuickSupplierModal
        open={quickSupplierModalOpen}
        onClose={() => setQuickSupplierModalOpen(false)}
        onSupplierCreated={(newSupplier) => {
          // Auto-select the newly created supplier
          setSelectedSupplier(newSupplier as Supplier);
          // Invalidate suppliers cache to refresh the dropdown
          queryClient.invalidateQueries({ queryKey: ['suppliers-active'] });
          queryClient.invalidateQueries({ queryKey: ['suppliers'] });
          showMessage('Fournisseur créé avec succès', 'success');
          setQuickSupplierModalOpen(false);
        }}
        colors={quickModalColors}
      />
    </Box>
  );
};

export default RestockCreate;
